import React, { useState } from 'react';
import { Avatar, Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setActiveCharacter } from '../api/characters';
import { useAuth } from '../services/AuthContext';
import Chevron from '../components/Chevron';
import { C, FONT, frameSx, revealSx } from '../theme/tokens';
import { CharacterSummary } from '../types';

/** Multi-character switcher — a flat bordered tile per character, the same
 * language every other list in the app uses. The stargate ring is
 * exclusive to the pre-auth login screen now, not duplicated here. */
const CharacterSelect: React.FC<{ characters: CharacterSummary[]; onConfirmed: () => void }> = ({
  characters,
  onConfirmed,
}) => {
  const { me, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<number | null>(null);

  const chooseMutation = useMutation({
    mutationFn: async (id: number) => {
      await setActiveCharacter(id);
      await refresh();
      await queryClient.invalidateQueries();
    },
    onSuccess: onConfirmed,
    onError: (_err, id) => setError(id),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', px: 2, py: 4 }}>
      <Typography variant="h1" sx={{ color: C.text, fontFamily: FONT.display, fontSize: '1.5rem', mb: 3 }}>
        Select Active Character
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 720 }}>
        {characters.map((c) => {
          const isCurrent = me.character?.id === c.eve_character_id;
          const isPending = chooseMutation.isPending && chooseMutation.variables === c.eve_character_id;
          const accent = isCurrent ? C.blue : C.cyan;
          return (
            <Box key={c.eve_character_id} sx={{ textAlign: 'center' }}>
              <ButtonBase
                onClick={() => {
                  setError(null);
                  chooseMutation.mutate(c.eve_character_id);
                }}
                disabled={isPending}
                sx={{
                  ...frameSx(accent),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  borderColor: isCurrent ? C.blue : undefined,
                  borderWidth: isCurrent ? 2 : 1,
                  width: 150,
                }}
              >
                {isPending ? (
                  <Box sx={{ width: 64, height: 64, mb: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={26} sx={{ color: C.blue }} />
                  </Box>
                ) : (
                  <Avatar
                    variant="square"
                    src={`https://images.evetech.net/characters/${c.eve_character_id}/portrait?size=128`}
                    sx={{ width: 64, height: 64, mb: 1.25, borderRadius: '2px', border: `1px solid ${C.hairlineBright}` }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Chevron color={accent} pulse={isCurrent} />
                  <Typography sx={{ color: C.text, fontWeight: 600, textAlign: 'center' }}>{c.name}</Typography>
                </Box>
                <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.75rem', textAlign: 'center' }}>
                  {c.corp_name ?? '—'}
                </Typography>
              </ButtonBase>
              {error === c.eve_character_id && (
                <Typography sx={{ color: C.red, fontSize: '0.7rem', mt: 0.5 }}>Couldn&rsquo;t switch — try again.</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CharacterSelect;
